import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Loading } from 'framework/ui/mui-builder';
import {
  CORE_SERVICE_PERSON_PARAM,
  CORE_SERVICE_STORAGE_PREFIX,
  OBLIGATION_TIER_LABELS,
  ORIGIN_CHANNEL_LABELS,
} from '../../constants';
import { useCoreServiceClient } from '../../api/coreServiceClient';
import useAsyncData from '../../hooks/useAsyncData';
import { useDebouncedValue } from 'framework/ViewTypes/hooks';
import { useCoreEvents } from '../../hooks/CoreEventsProvider';
import EmptyState from '../../components/EmptyState';
import ErrorNotice from '../../components/ErrorNotice';
import OriginDot from '../../components/OriginDot';
import SectionLabel from '../../components/SectionLabel';
import SlaText from '../../components/SlaText';
import StateChip from '../../components/StateChip';
import type { ObligationDto, OriginDto, PersonDto } from '../../types';
import { formatNumber, getErrorMessage, isNotFound, loadStoredValue, saveStoredValue } from '../../utils/format';
import { parseIsoDurationSeconds } from '../../utils/sla';

const PERSON_STORAGE_KEY = `${CORE_SERVICE_STORAGE_PREFIX}.person`;
const EVENTS_RELOAD_DELAY_MS = 500;

const getPersonLabel = (person: PersonDto) => `${person.name} (${person.login})`;

const OriginText = ({ origin, currentLogin }: { origin?: Nullable<OriginDto>; currentLogin: string }) => {
  if (!origin) {
    return null;
  }

  if (origin.kind === 'PROCESS') {
    return (
      <Box component="span" sx={{ color: 'secondary.main' }}>
        {' · '}from process: {origin.process}
        {origin.businessKey ? ` — ${origin.businessKey}` : ''}
      </Box>
    );
  }

  const text =
    origin.initiatorLogin && origin.initiatorLogin === currentLogin
      ? 'self-assigned'
      : `asked by ${origin.initiator || ORIGIN_CHANNEL_LABELS[origin.channel] || 'unknown'}`;

  return (
    <Box component="span" sx={{ color: 'info.main' }}>
      {' · '}
      {text}
    </Box>
  );
};

const ObligationCard = ({ obligation, currentLogin }: { obligation: ObligationDto; currentLogin: string }) => {
  const remaining = parseIsoDurationSeconds(obligation.slaRemaining);

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'grid',
        gridTemplateColumns: '36px 1fr auto',
        gap: 1.5,
        alignItems: 'start',
        padding: '10px 14px',
        marginBottom: 1,
        borderColor: 'border.default',
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        #{obligation.rank}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          {obligation.url ? (
            <Link href={obligation.url} target="_blank" rel="noopener noreferrer" color="inherit" underline="hover">
              {obligation.title}
            </Link>
          ) : (
            obligation.title
          )}
        </Typography>
        <Stack direction="row" gap={0.5} flexWrap="wrap" alignItems="center" sx={{ marginTop: 0.5 }}>
          <StateChip label={obligation.definitionName} />
          <StateChip label={obligation.stateName} />
          {obligation.strength === 'DYNAMIC' ? <StateChip label={`queue · ${obligation.coOwners + 1} people`} /> : null}
          <Typography variant="caption" color="text.secondary">
            {(obligation.path || []).join(' / ')}
            <OriginText origin={obligation.origin} currentLogin={currentLogin} />
          </Typography>
        </Stack>
      </Box>
      <SlaText
        seconds={remaining}
        fallback={obligation.stateKind === 'TRANSIENT' ? 'no SLA yet' : undefined}
        dueDate={obligation.dueDate}
      />
    </Paper>
  );
};

const TodayTab = () => {
  const client = useCoreServiceClient();
  const { lastSeq } = useCoreEvents();
  const [searchParams, setSearchParams] = useSearchParams();
  const [login, setLogin] = useState<string>(
    () => searchParams.get(CORE_SERVICE_PERSON_PARAM) || loadStoredValue(PERSON_STORAGE_KEY) || '',
  );
  const [includeSteady, setIncludeSteady] = useState(true);

  const people = useAsyncData(() => client.listPeople().then(response => response.result ?? []), [client]);
  const peopleOptions = useMemo(() => people.data ?? [], [people.data]);
  const selectedPerson = useMemo(
    () => peopleOptions.find(person => person.login === login) ?? null,
    [peopleOptions, login],
  );

  const obligations = useAsyncData(
    () => client.getObligations(login, includeSteady),
    [client, login, includeSteady],
    !!login,
  );

  const selectPerson = useCallback(
    (person: Nullable<PersonDto>) => {
      const next = person?.login ?? '';
      const params = new URLSearchParams(searchParams);

      if (next) {
        params.set(CORE_SERVICE_PERSON_PARAM, next);
      } else {
        params.delete(CORE_SERVICE_PERSON_PARAM);
      }

      setLogin(next);
      saveStoredValue(PERSON_STORAGE_KEY, next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Live refresh: the event log moved, so the person's list may have changed.
  const debouncedSeq = useDebouncedValue(lastSeq, EVENTS_RELOAD_DELAY_MS);
  const reloadRef = useRef(obligations.reload);
  const seenSeqRef = useRef(debouncedSeq);

  reloadRef.current = obligations.reload;

  useEffect(() => {
    if (debouncedSeq !== seenSeqRef.current) {
      seenSeqRef.current = debouncedSeq;

      if (login) {
        reloadRef.current();
      }
    }
  }, [debouncedSeq, login]);

  const data = obligations.data;
  const groups = useMemo(() => {
    const result: { tier: string; items: ObligationDto[] }[] = [];

    (data?.obligations ?? []).forEach(obligation => {
      const last = result[result.length - 1];

      if (last && last.tier === obligation.tier) {
        last.items.push(obligation);
      } else {
        result.push({ tier: obligation.tier, items: [obligation] });
      }
    });

    return result;
  }, [data]);

  const renderBody = () => {
    if (!login) {
      return <EmptyState>Pick a person to see their obligations, ordered by ownership strength and SLA.</EmptyState>;
    }

    if (obligations.error && isNotFound(obligations.error)) {
      return <EmptyState>Person not found</EmptyState>;
    }

    if (obligations.error) {
      return <ErrorNotice error={obligations.error} onRetry={obligations.reload} />;
    }

    if (!data) {
      return <Loading isInitialPosition />;
    }

    if (!data.obligations.length) {
      return <EmptyState>Nothing owned right now.</EmptyState>;
    }

    return groups.map(group => (
      <Box key={group.tier}>
        <SectionLabel>{OBLIGATION_TIER_LABELS[group.tier] || group.tier}</SectionLabel>
        {group.items.map(obligation => (
          <ObligationCard key={obligation.id} obligation={obligation} currentLogin={login} />
        ))}
      </Box>
    ));
  };

  return (
    <Box>
      <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap" sx={{ marginBottom: 2 }}>
        <Autocomplete
          size="small"
          sx={{ width: 320 }}
          options={peopleOptions}
          loading={people.isLoading}
          value={selectedPerson}
          getOptionLabel={getPersonLabel}
          isOptionEqualToValue={(option, value) => option.login === value.login}
          onChange={(_, person) => selectPerson(person)}
          renderInput={params => (
            <TextField
              {...params}
              label="View as"
              placeholder="login or name"
              error={!!people.error}
              helperText={people.error ? getErrorMessage(people.error) : undefined}
            />
          )}
        />
        <FormControlLabel
          label="include steady states"
          control={<Checkbox checked={includeSteady} onChange={event => setIncludeSteady(event.target.checked)} />}
        />
        {data && login ? (
          <Typography variant="body2" color="text.secondary">
            {data.person.name}: {formatNumber(data.total)} obligations, {formatNumber(data.overdue)} overdue
          </Typography>
        ) : null}
      </Stack>
      {renderBody()}
      {login && selectedPerson === null && !people.isLoading && !people.error ? (
        <Typography variant="caption" color="text.secondary">
          <OriginDot kind="person" />
          {login} is not in the people list; showing the stored selection.
        </Typography>
      ) : null}
    </Box>
  );
};

export default TodayTab;
